import axios, { AxiosError, AxiosInstance } from 'axios';
import React, { createContext, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';

type ContextValue = AxiosInstance;

type AxiosContextType = {
  authAxios: ContextValue;
  publicAxios: ContextValue;
};

const contextErr = () => {
  throw new Error('You must wrap your component in an AxiosProvider');
};

const contextProxy = () => {
  return new Proxy(axios.create(), {
    apply: () => contextErr(),
    get: () => contextErr()
  });
};

const AxiosContext = createContext<AxiosContextType>({
  authAxios: contextProxy(),
  publicAxios: contextProxy()
});

const { Provider } = AxiosContext;

export function AxiosProvider({ children }: { children: React.ReactNode }) {
  const publicAxios = axios.create();
  const authAxios = axios.create({
    withCredentials: true,
    baseURL: process.env.REACT_APP_API_BASE_URL
  });

  // Handling 500 errors
  const handleFiveHundredErrors = async (error: AxiosError) => {
    const status = error && error.response ? error.response.status : 0;

    // NOTE: 401/403 errors are being handled in AuthContext.tsx

    // TODO Change the error title to something more understandable
    if (status >= 500 && status <= 599) {
      toast.error('We have an internal problem... Request failed', { position: 'top-center' });
      return error;
    }
    return Promise.reject(error);
  };

  authAxios.interceptors.response.use((response) => response, handleFiveHundredErrors);
  publicAxios.interceptors.response.use((response) => response, handleFiveHundredErrors);

  // Disabled until we have X-CSRF token support on the back-end
  useEffect(() => {
    const getCsrfToken = async () => {
      try {
        const { data } = await authAxios.get('/csrf-token');
        authAxios.defaults.headers['X-CSRF-Token'] = data.crumb;
      } catch (err) {
        // Do nothing
      }
    };

    getCsrfToken();
  }, [authAxios, publicAxios]);

  return (
    <Provider
      value={{
        authAxios,
        publicAxios
      }}
    >
      {children}
    </Provider>
  );
}

export function useAxios() {
  const context = useContext(AxiosContext);
  if (context === undefined) {
    throw new Error('The component this hook must be a descendant of the AxiosProvider');
  }
  return context;
}
